import React, { useEffect, useState } from 'react'
import { Checkbox, Icon, Tooltip, Select, DatePicker } from 'antd'
import './ReminderTriggers.scss'

const { Option } = Select

const ReminderTriggers = ({
  onChange,
  startValue,
  endValue,
  is_mandatory,
  is_retried,
  is_recurrence,
  mandatory_reminder = null,
  quiz_retried_reminder = null,
  recurrence_reminder = null
}) => {
  const [checkedItems, setCheckedItems] = useState({
    completion: is_mandatory,
    calendar: is_recurrence,
    failed: is_retried
  })

  const [expandedItems, setExpandedItems] = useState({
    completion: false,
    calendar: false,
    failed: false
  })

  const [collapseAll, setCollapseAll] = useState(false)
  const [endOpen, setEndOpen] = useState(false)

  const [completionInterval, setCompletionInterval] = useState('undefined')
  const [completionNumber, setCompletionNumber] = useState('undefined')
  const [completionDueDate, setCompletionDueDate] = useState('undefined')
  const [calendarInterval, setCalendarInterval] = useState('undefined')
  const [calendarNumber, setCalendarNumber] = useState('undefined')
  const [calendarDueDate, setCalendarDueDate] = useState('undefined')
  const [failedInterval, setFailedInterval] = useState('undefined')
  const [failedNumber, setFailedNumber] = useState('undefined')
  const [failedDueDate, setFailedDueDate] = useState('undefined')

  useEffect(() => {
    setCheckedItems({
      completion: is_mandatory,
      calendar: is_recurrence,
      failed: is_retried
    })

    setCompletionInterval(mandatory_reminder?.interval)
    setCompletionNumber(mandatory_reminder?.interval_count)
    setCompletionDueDate(mandatory_reminder?.due_date)

    setCalendarInterval(recurrence_reminder?.interval)
    setCalendarNumber(recurrence_reminder?.interval_count)
    setCalendarDueDate(recurrence_reminder?.due_date)

    setFailedInterval(quiz_retried_reminder?.interval)
    setFailedNumber(quiz_retried_reminder?.interval_count)
    setFailedDueDate(quiz_retried_reminder?.due_date)
  }, [
    is_mandatory,
    is_recurrence,
    is_retried,
    mandatory_reminder,
    recurrence_reminder,
    quiz_retried_reminder
  ])

  const disabledStartDate = startValue => {
    if (!startValue || !endValue) {
      return false
    }
    return startValue.valueOf() > endValue.valueOf()
  }

  const disabledEndDate = endValue => {
    if (!endValue || !startValue) {
      return false
    }
    return endValue.valueOf() <= startValue.valueOf()
  }

  const handleDateChange = (field, value) => {
    if (onChange) {
      onChange(field, value)
    }
  }

  const onStartChange = value => {
    handleDateChange('startValue', value)
    if (value) {
      setEndOpen(true)
    }
  }

  const onEndChange = value => {
    handleDateChange('endValue', value)
  }

  const handleEndOpenChange = open => {
    setEndOpen(open)
  }

  const getNumberOptions = () => {
    return Array.from({ length: 10 }, (_, i) => i + 1)
  }

  const handleCheckbox = key => {
    setCheckedItems(prev => {
      const next = {
        ...prev,
        [key]: !prev[key]
      }

      if (onChange) {
        if (key === 'completion') {
          if (!next.completion) {
            onChange('mandatory_reminder', null)
          } else {
            onChange('mandatory_reminder', {
              interval: completionInterval,
              interval_count: parseInt(completionNumber, 10),
              due_date: completionDueDate
            })
          }
        }

        if (key === 'calendar') {
          if (!next.calendar) {
            onChange('recurrence_reminder', null)
          } else {
            onChange('recurrence_reminder', {
              interval: calendarInterval,
              interval_count: parseInt(calendarNumber, 10),
              due_date: calendarDueDate
            })
          }
        }

        if (key === 'failed') {
          if (!next.failed) {
            onChange('quiz_retried_reminder', null)
          } else {
            onChange('quiz_retried_reminder', {
              interval: failedInterval,
              interval_count: parseInt(failedNumber, 10),
              due_date: failedDueDate
            })
          }
        }
      }

      return next
    })
  }

  const toggleExpand = (key, e) => {
    e.stopPropagation()
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  const toggleCollapseAll = () => {
    const newState = !collapseAll
    setCollapseAll(newState)
    if (newState) {
      setExpandedItems({
        completion: true,
        calendar: true,
        failed: true
      })
    } else {
      setExpandedItems({
        completion: false,
        calendar: false,
        failed: false
      })
    }
  }

  return (
    <div className="reminder-triggers">
      <div className="reminder-triggers__container">
        <div className="reminder-triggers__date-section">
          <div className="reminder-triggers__date-field">
            <label className="reminder-triggers__label">Start Date</label>
            <DatePicker
              style={{ width: '100%' }}
              disabledDate={disabledStartDate}
              format="DD-MM-YYYY"
              value={startValue}
              placeholder="Select start date"
              onChange={onStartChange}
              suffixIcon={<Icon type="calendar" />}
              className="reminder-triggers__date-picker"
            />
          </div>
          <div className="reminder-triggers__date-field">
            <label className="reminder-triggers__label">Expiry Date</label>
            <DatePicker
              style={{ width: '100%' }}
              disabledDate={disabledEndDate}
              format="DD-MM-YYYY"
              value={endValue}
              placeholder="Select expiry date"
              onChange={onEndChange}
              open={endOpen}
              onOpenChange={handleEndOpenChange}
              suffixIcon={<Icon type="calendar" />}
              className="reminder-triggers__date-picker"
            />
          </div>
        </div>

        <div className="reminder-triggers__triggers-section">
          <div className="reminder-triggers__header">
            <h2 className="reminder-triggers__title">
              Reminder Triggers & Frequency
            </h2>
            <div className="reminder-triggers__collapse-toggle">
              <span className="reminder-triggers__collapse-label">
                Expand All
              </span>
              <div
                className={`reminder-triggers__toggle ${collapseAll ? 'reminder-triggers__toggle--on' : ''
                  }`}
                onClick={toggleCollapseAll}
              >
                <div className="reminder-triggers__toggle-slider" />
              </div>
            </div>
          </div>

          <div className="reminder-triggers__cards">
            <div className="reminder-triggers__card">
              <div className="reminder-triggers__card-header">
                <div className="reminder-triggers__card-label-group">
                  <Checkbox
                    checked={checkedItems.calendar}
                    onChange={e => {
                      e.stopPropagation()
                      onChange('is_recurrence', !is_recurrence)
                      handleCheckbox('calendar')
                    }}
                    className="reminder-triggers__checkbox"
                  />
                  <span className="reminder-triggers__card-label">
                    Recurrence by Date
                  </span>
                </div>
                <Icon
                  type={expandedItems.calendar ? 'up' : 'down'}
                  onClick={e => toggleExpand('calendar', e)}
                  className="reminder-triggers__expand-icon"
                />
              </div>
              {expandedItems.calendar && (
                <div className="reminder-triggers__card-content">
                  <div className="reminder-triggers__radio-group">
                    <div className="reminder-triggers__radio-row">
                      <div>Every</div>
                      <Select
                        placeholder="Select interval"
                        className="reminder-triggers__select"
                        suffixIcon={<Icon type="down" />}
                        allowClear
                        showSearch
                        value={calendarNumber}
                        onChange={value => {
                          setCalendarNumber(value)
                          if (onChange && checkedItems.calendar) {
                            onChange('recurrence_reminder', {
                              interval: calendarInterval,
                              interval_count: parseInt(value, 10),
                              due_date: calendarDueDate
                            })
                          }
                        }}
                      >
                        {getNumberOptions().map(num => (
                          <Option key={num} value={String(num)}>
                            {num}
                          </Option>
                        ))}
                      </Select>
                      <Select
                        className="reminder-triggers__select"
                        suffixIcon={<Icon type="down" />}
                        placeholder="Select duration"
                        value={calendarInterval}
                        onChange={value => {
                          setCalendarInterval(value)
                          if (onChange && checkedItems.calendar) {
                            onChange('recurrence_reminder', {
                              interval: value,
                              interval_count: parseInt(calendarNumber, 10),
                              due_date: calendarDueDate
                            })
                          }
                        }}
                      >
                        <Option value="day">Day</Option>
                        <Option value="week">Week</Option>
                        <Option value="month">Month</Option>
                        <Option value="year">Year</Option>
                      </Select>
                    </div>
                    <div className="reminder-triggers__radio-row reminder-triggers__radio-row--full-width">
                      <div>Due Date</div>
                      <DatePicker
                        placeholder="Select date"
                        format="DD-MM-YYYY"
                        suffixIcon={<Icon type="calendar" />}
                        className="reminder-triggers__date-picker reminder-triggers__date-picker--full"
                        value={calendarDueDate}
                        onChange={date => {
                          setCalendarDueDate(date)
                          if (onChange && checkedItems.calendar) {
                            onChange('recurrence_reminder', {
                              interval: calendarInterval,
                              interval_count: parseInt(calendarNumber, 10),
                              due_date: date
                            })
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="reminder-triggers__card">
              <div className="reminder-triggers__card-header">
                <div className="reminder-triggers__card-label-group">
                  <Checkbox
                    checked={checkedItems.completion}
                    onChange={e => {
                      e.stopPropagation()
                      onChange('is_mandatory', !is_mandatory)
                      handleCheckbox('completion')
                    }}
                    className="reminder-triggers__checkbox"
                  />
                  <span className="reminder-triggers__card-label">
                    Mandatory Course
                  </span>
                  <Tooltip
                    style={{
                      background: '#EDF2F5',
                      maxWidth: '300px'
                    }}
                    title={
                      <div
                        style={{
                          padding: '1.2rem'
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 600,
                            marginBottom: '6px',
                            fontSize: '14px',
                            color: '#5c6cff',
                            lineHeight: '1.25'
                          }}
                        >
                          Start date
                        </div>

                        <div
                          style={{
                            fontSize: '13px',
                            color: '#676973',
                            lineHeight: '1.35'
                          }}
                        >
                          This rule uses the course's global Start Date set on
                          the left.
                        </div>
                      </div>
                    }
                  >
                    <Icon
                      type="info-circle"
                      className="reminder-triggers__info-icon"
                      onClick={e => e.stopPropagation()}
                    />
                  </Tooltip>
                </div>
                <Icon
                  type={expandedItems.completion ? 'up' : 'down'}
                  onClick={e => toggleExpand('completion', e)}
                  className="reminder-triggers__expand-icon"
                />
              </div>
              {expandedItems.completion && (
                <div className="reminder-triggers__card-content">
                  <div className="reminder-triggers__radio-group">
                    <div className="reminder-triggers__radio-row">
                      <div>Every</div>
                      <Select
                        placeholder="Select interval"
                        className="reminder-triggers__select"
                        suffixIcon={<Icon type="down" />}
                        allowClear
                        showSearch
                        value={completionNumber}
                        onChange={value => {
                          setCompletionNumber(value)
                          if (onChange && checkedItems.completion) {
                            onChange('mandatory_reminder', {
                              interval: completionInterval,
                              interval_count: parseInt(value, 10),
                              due_date: completionDueDate
                            })
                          }
                        }}
                      >
                        {getNumberOptions().map(num => (
                          <Option key={num} value={String(num)}>
                            {num}
                          </Option>
                        ))}
                      </Select>
                      <Select
                        className="reminder-triggers__select"
                        suffixIcon={<Icon type="down" />}
                        placeholder="Select duration"
                        value={completionInterval}
                        onChange={value => {
                          setCompletionInterval(value)
                          if (onChange && checkedItems.completion) {
                            onChange('mandatory_reminder', {
                              interval: value,
                              interval_count: parseInt(completionNumber, 10),
                              due_date: completionDueDate
                            })
                          }
                        }}
                      >
                        <Option value="day">Day</Option>
                        <Option value="week">Week</Option>
                        <Option value="month">Month</Option>
                        <Option value="year">Year</Option>
                      </Select>
                    </div>
                    <div className="reminder-triggers__radio-row reminder-triggers__radio-row--full-width">
                      <div>Due Date</div>
                      <DatePicker
                        placeholder="Select date"
                        format="DD-MM-YYYY"
                        suffixIcon={<Icon type="calendar" />}
                        className="reminder-triggers__date-picker reminder-triggers__date-picker--full"
                        value={completionDueDate}
                        onChange={date => {
                          setCompletionDueDate(date)
                          if (onChange && checkedItems.completion) {
                            onChange('mandatory_reminder', {
                              interval: completionInterval,
                              interval_count: parseInt(completionNumber, 10),
                              due_date: date
                            })
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="reminder-triggers__card">
              <div className="reminder-triggers__card-header">
                <div className="reminder-triggers__card-label-group">
                  <Checkbox
                    checked={checkedItems.failed}
                    onChange={e => {
                      e.stopPropagation()
                      onChange('is_retried', !is_retried)
                      handleCheckbox('failed')
                    }}
                    className="reminder-triggers__checkbox"
                  />
                  <span className="reminder-triggers__card-label">
                    By failed attempts
                  </span>
                </div>
                <Icon
                  type={expandedItems.failed ? 'up' : 'down'}
                  onClick={e => toggleExpand('failed', e)}
                  className="reminder-triggers__expand-icon"
                />
              </div>
              {expandedItems.failed && (
                <div className="reminder-triggers__card-content">
                  <div className="reminder-triggers__radio-group">
                    <div className="reminder-triggers__radio-row">
                      <div>Every</div>
                      <Select
                        placeholder="Select interval"
                        className="reminder-triggers__select"
                        suffixIcon={<Icon type="down" />}
                        allowClear
                        showSearch
                        value={failedNumber}
                        onChange={value => {
                          setFailedNumber(value)
                          if (onChange && checkedItems.failed) {
                            onChange('quiz_retried_reminder', {
                              interval: failedInterval,
                              interval_count: parseInt(value, 10),
                              due_date: failedDueDate
                            })
                          }
                        }}
                      >
                        {getNumberOptions().map(num => (
                          <Option key={num} value={String(num)}>
                            {num}
                          </Option>
                        ))}
                      </Select>
                      <Select
                        className="reminder-triggers__select"
                        suffixIcon={<Icon type="down" />}
                        value={failedInterval}
                        placeholder="Select duration"
                        onChange={value => {
                          setFailedInterval(value)
                          if (onChange && checkedItems.failed) {
                            onChange('quiz_retried_reminder', {
                              interval: value,
                              interval_count: parseInt(failedNumber, 10),
                              due_date: failedDueDate
                            })
                          }
                        }}
                      >
                        <Option value="day">Day</Option>
                        <Option value="week">Week</Option>
                        <Option value="month">Month</Option>
                        <Option value="year">Year</Option>
                      </Select>
                    </div>
                    <div className="reminder-triggers__radio-row reminder-triggers__radio-row--full-width">
                      <div>Due Date</div>
                      <DatePicker
                        placeholder="Select date"
                        format="DD-MM-YYYY"
                        suffixIcon={<Icon type="calendar" />}
                        className="reminder-triggers__date-picker reminder-triggers__date-picker--full"
                        value={failedDueDate}
                        onChange={date => {
                          setFailedDueDate(date)
                          if (onChange && checkedItems.failed) {
                            onChange('quiz_retried_reminder', {
                              interval: failedInterval,
                              interval_count: parseInt(failedNumber, 10),
                              due_date: date
                            })
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <hr className="reminder-triggers__divider" />
    </div>
  )
}

export default ReminderTriggers
